<script setup lang="ts">
import { computed, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import Banner from '@/components/ComplexBanner.vue'
import { examModule } from '@/store/exam/module'
import { organizationModule } from '@/store/organization/module'
import { analyticsModule } from '@/store/analytics/module'
import { isSupportWindow } from '@/utils'
import PurchaseSeatsModal from '@/components/SeatsModal/PurchaseSeatsModal.vue'

const {
    Button: PocketButton,
} = UIKit

const props = withDefaults(
    defineProps<{
        numStudentsMissingLicenses?: number
    }>(),
    { numStudentsMissingLicenses: 0 }
)

const showPurchaseSeatsModal = ref(false)

const areAllOrgExamsFree = computed(() => examModule.getters.getAreAllOrgExamsFree())

const currentOrganization = computed(() => organizationModule.getters.getCurrentOrganization())

const showPurchaseSeatsLink = computed(() => {
    // Only show if the current organization is newer than May 1, 2024
    return !areAllOrgExamsFree.value
        && organizationModule.getters.getHasSelfServicePaymentAccess()
})

const closePurchaseSeatsModal = () => {
    showPurchaseSeatsModal.value = false
}

const showPurchaseSeatsSidePanel = () => {
    showPurchaseSeatsModal.value = true

    if (currentOrganization.value) {
        // Fire amplitude event
        analyticsModule.actions.amplitudeTrack('Side Panel Opened', {
            sidePanelName: 'Purchase Seats',
            sidePanelPlacement: 'No Seats Banner',
            organizationId: currentOrganization.value.objectId,
            organizationName: currentOrganization.value.name,
        })
    }
}

const showIntercom = () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('show')
    }
}
</script>

<template>
    <div class="no-seats-banner">
        <PurchaseSeatsModal
            v-if="showPurchaseSeatsModal"
            @close="closePurchaseSeatsModal"
        />
        <Banner
            class="no-seats-banner__no-available-seats"
            :is-red-banner="true"
        >
            You have 0 available seats for students. There are currently
            {{ props.numStudentsMissingLicenses }} active students with Free prep.
            <PocketButton
                v-if="showPurchaseSeatsLink"
                type="tertiary"
                @click="showPurchaseSeatsSidePanel"
            >
                Purchase Seats
            </PocketButton>
            <span
                v-else
            >
                Reach out to
                <PocketButton
                    type="tertiary"
                    @click="showIntercom"
                    @keydown.enter="showIntercom"
                >
                    customer success
                </PocketButton> to purchase more seats.
            </span>
            <template #link>
                <div />
            </template>
        </Banner>
    </div>
</template>

<style lang="scss" scoped>
    .no-seats-banner {
        margin-bottom: 19px;

        :deep(*) {
            .banner__text {
                margin: 0;
            }
        }

        &__no-available-seats {
            font-size: 15px;
            box-sizing: border-box;
        }
    }
</style>
